/* Comparison of Supplier Price Offers.
 *
 * Workflow:
 *   1) Pick Customer + Project + Type of Work + Date
 *   2) System lists matching Customer RFQs → user picks items to bring in
 *   3) For each supplier column:
 *        - Pick Supplier → pick one of their Vendor Quotations
 *        - Table auto-fills (Unit Price + Qty + Total + Lead Time)
 *   4) Add Supplier button creates the next supplier column
 *   5) Save (Draft or final) → row appears in the list
 *   6) Click row → detail modal
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import {
  Plus, Trash2, X, Scale, Save, Eye, Download, RefreshCw,
  CheckCircle, AlertCircle,
} from 'lucide-react';

const STATUS_TONE = {
  draft:                 'bg-gray-100 text-gray-700',
  saved:                 'bg-emerald-100 text-emerald-900',
  awarded:               'bg-violet-100 text-violet-900',
  pending_procurement:   'bg-amber-100 text-amber-900',
  pending_finance:       'bg-amber-100 text-amber-900',
  approved:              'bg-emerald-200 text-emerald-900',
  rejected:              'bg-rose-200 text-rose-900',
  cancelled:             'bg-rose-100 text-rose-800',
};

// Category code → human label (mirrors backend services/customer_rfq.py).
const CATEGORY_LABELS = {
  FS: 'Fire Stop Sealant',
  EJ: 'Expansion Joint Cover',
  RF: 'Raised Floor',
  TP: 'Toilet Partition Phenolic',
  PV: 'PVC Handrail',
  RP: 'Rubber Protection',
  SN: 'Stair Nosing',
  FM: 'Floor Mat',
  SD: 'Steel Door',
  OT: 'Other',
};
const formatCategory = (code) => {
  if (!code) return '—';
  const label = CATEGORY_LABELS[code];
  return label ? `${code} - ${label}` : code;
};

// 7-bucket Payment Terms structure (mirrors VendorQuotations form).
const PT_FIELDS = [
  { key: 'advance_payment',             label: 'Advance Payment' },
  { key: 'design_engineering',          label: 'Design & Engineering' },
  { key: 'after_delivery',              label: 'After Delivery' },
  { key: 'before_delivery',             label: 'Before Delivery' },
  { key: 'after_installation',          label: 'After Installation' },
  { key: 'after_testing_commissioning', label: 'After Testing & Commissioning' },
  { key: 'retention',                   label: 'Retention' },
  { key: 'other_milestone_1',           label: 'Other Milestone 1' },
  { key: 'other_milestone_2',           label: 'Other Milestone 2' },
];
const blankBreakdown = () => Object.fromEntries(PT_FIELDS.map(f => [f.key, '']));
// Summary text built from non-zero buckets (e.g. "Advance Payment 30% · Retention 10%").
const summarizeBreakdown = (bd) => PT_FIELDS
  .filter(f => Number((bd || {})[f.key]) > 0)
  .map(f => `${f.label} ${Number(bd[f.key])}%`)
  .join(' · ');


// ───────────────────────── List ──────────────────────────────
const PriceComparison = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [approvalCfg, setApprovalCfg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/price-comparisons?limit=300');
      setRows(r.data?.comparisons || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Load the role-based approval config once. Drives which Approve /
  // Reject buttons render inside the ComparisonDetail modal.
  useEffect(() => {
    axiosInstance.get('/price-comparisons/meta/approval-config')
      .then((r) => setApprovalCfg(r.data))
      .catch(() => setApprovalCfg(null));
  }, []);

  const openDetail = async (id) => {
    try {
      const r = await axiosInstance.get(`/price-comparisons/${id}`);
      setViewing(r.data);
    } catch (_) { toast.error('Failed to load comparison'); }
  };

  const refreshDetail = async (id) => {
    try {
      const r = await axiosInstance.get(`/price-comparisons/${id}`);
      setViewing(r.data);
      load();
    } catch (_) { /* ignore */ }
  };

  const downloadPdf = async (row) => {
    try {
      const r = await axiosInstance.get(`/price-comparisons/${row.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${row.ref_no || 'Comparison'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) { toast.error('PDF export failed'); }
  };

  const revise = async (row) => {
    try {
      const r    = await axiosInstance.get(`/price-comparisons/${row.id}`);
      const orig = r.data;
      // Clone the comparison as a fresh draft so the user gets a new ref_no
      // while keeping every line, supplier, qty and the price grid intact.
      const clone = {
        ...orig,
        id:                undefined,
        ref_no:            undefined,
        status:            'draft',
        notes:             orig.notes
          ? `${orig.notes}\n\n(Revised from ${orig.ref_no})`
          : `Revised from ${orig.ref_no}`,
        revised_from_id:   orig.id,
        revised_from_ref:  orig.ref_no,
      };
      setEditing(clone);
      setShowForm(true);
    } catch (_) { toast.error('Failed to load comparison for revision'); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="price-comparison-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold inline-flex items-center gap-2">
          <Scale className="w-6 h-6" /> Comparison of Supplier Price Offers
          <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-900 text-white"
                data-testid="price-comparison-count">{rows.length}</span>
        </h1>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
                data-testid="price-comparison-new-button"
                className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New Comparison
        </button>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Ref</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Project</th>
              <th className="text-left px-3 py-2">Type of Work</th>
              <th className="text-right px-3 py-2">Items</th>
              <th className="text-right px-3 py-2">Suppliers</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 w-20">PDF</th>
              <th className="text-right px-3 py-2 w-24">Revise</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="text-center text-gray-400 py-8">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="text-center text-gray-400 py-8" data-testid="price-comparison-empty">
                No comparisons yet — click <strong>New Comparison</strong>.
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} onClick={() => openDetail(r.id)}
                  data-testid={`price-comparison-row-${r.ref_no}`}
                  className="border-t border-gray-100 cursor-pointer hover:bg-indigo-50/30">
                <td className="px-3 py-2 font-mono text-xs">{r.ref_no}</td>
                <td className="px-3 py-2 text-xs">{r.date}</td>
                <td className="px-3 py-2">{r.customer_name || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.project_name || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.type_of_work || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.items_count}</td>
                <td className="px-3 py-2 text-right font-mono">{r.suppliers_count}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[r.status] || STATUS_TONE.draft}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => downloadPdf(r)}
                          data-testid={`price-comparison-pdf-${r.ref_no}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50">
                    <Download className="w-3 h-3" /> PDF
                  </button>
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => revise(r)}
                          data-testid={`price-comparison-revise-${r.ref_no}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border border-amber-200 text-amber-700 hover:bg-amber-50">
                    <RefreshCw className="w-3 h-3" /> Revise
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ComparisonForm existing={editing}
                         onClose={() => { setShowForm(false); setEditing(null); }}
                         onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}

      {viewing && (
        <ComparisonDetail comparison={viewing}
                           approvalCfg={approvalCfg}
                           onClose={() => setViewing(null)}
                           onRefresh={() => refreshDetail(viewing.id)}
                           onEdit={() => { setEditing(viewing); setViewing(null); setShowForm(true); }} />
      )}
    </div>
  );
};


// ────────────────────── Form Modal ───────────────────────────
const ComparisonForm = ({ existing, onClose, onSaved }) => {
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects]   = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [availableRfqs, setAvailableRfqs] = useState([]);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState(() => existing ? {
    date:          existing.date,
    customer_id:   existing.customer_id || '',
    project_id:    existing.project_id || '',
    type_of_work:  existing.type_of_work || '',
    notes:         existing.notes || '',
    source_customer_rfq_ids: existing.source_customer_rfq_ids || [],
    items:         existing.items || [],
    suppliers:     existing.suppliers || [],
  } : {
    date:          new Date().toISOString().slice(0, 10),
    customer_id:   '',
    project_id:    '',
    type_of_work:  '',
    notes:         '',
    source_customer_rfq_ids: [],
    items:         [],
    suppliers:     [],
  });

  // When opening from an existing comparison (Revise / Edit), re-autofill
  // every supplier column once on mount so newly-introduced fields like
  // `type_of_work` and `install_price` get surfaced even on older comparisons.
  useEffect(() => {
    if (existing && (existing.suppliers || []).length > 0) {
      refreshAllSupplierColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial lookups.
  useEffect(() => {
    axiosInstance.get('/customers?limit=500').catch(() => ({data: {customers: []}}))
      .then(r => setCustomers(r.data?.customers || (Array.isArray(r.data) ? r.data : [])));
    axiosInstance.get('/projects?limit=500').catch(() => ({data: []}))
      .then(r => setProjects(Array.isArray(r.data) ? r.data : (r.data?.projects || [])));
    axiosInstance.get('/suppliers?limit=500').catch(() => ({data: {suppliers: []}}))
      .then(r => setSuppliers(r.data?.suppliers || (Array.isArray(r.data) ? r.data : [])));
  }, []);

  // Refresh the candidate-RFQ list whenever customer/project change.
  useEffect(() => {
    const params = new URLSearchParams();
    if (form.customer_id) params.append('customer_id', form.customer_id);
    if (form.project_id)  params.append('project_id', form.project_id);
    axiosInstance.get(`/price-comparisons/meta/customer-rfqs?${params}`)
      .then(r => setAvailableRfqs(r.data?.customer_rfqs || []))
      .catch(() => setAvailableRfqs([]));
  }, [form.customer_id, form.project_id]);

  const importLinesFromRfq = (rfq, lineIdsToAdd) => {
    const newItems = (rfq.lines || [])
      .filter(ln => lineIdsToAdd.includes(`${rfq.id}:${ln.no}`))
      .map(ln => ({
        source_rfq_id: rfq.id,
        source_rfq_no: rfq.rfq_no,
        source_line_no: ln.no,
        no:          (form.items.length || 0) + 1,   // placeholder — renumbered below
        item_ref:    ln.item_ref,
        category:    ln.category,
        type_of_work: ln.type_of_work,
        description: ln.description,
        size:        ln.size,
        unit:        ln.unit,
        qty:         ln.qty,
        requested_brand:  ln.requested_brand,
        brand_strictness: ln.brand_strictness,
      }));
    setForm(f => {
      const items = [...f.items, ...newItems].map((it, i) => ({...it, no: i + 1}));
      const rfqIds = Array.from(new Set([...(f.source_customer_rfq_ids || []), rfq.id]));
      return {...f, items, source_customer_rfq_ids: rfqIds};
    });
    // Re-autofill any already-attached supplier columns since items changed.
    refreshAllSupplierColumns(newItems);
  };

  const removeItem = (no) => {
    setForm(f => {
      const items = f.items.filter(it => it.no !== no).map((it, i) => ({...it, no: i + 1}));
      return {...f, items};
    });
    refreshAllSupplierColumns();
  };

  const refreshAllSupplierColumns = async (_addedItems = null) => {
    // Re-autofill every supplier so they re-align to the new item set.
    setForm(f => {
      Promise.all((f.suppliers || []).map(async (sup) => {
        if (!sup.vendor_quote_id) return sup;
        try {
          const r = await axiosInstance.post('/price-comparisons/autofill-supplier-column', {
            vendor_quote_id: sup.vendor_quote_id, items: f.items,
          });
          return { ...sup,
                    rows: r.data.rows,
                    payment_terms: sup.payment_terms || r.data.payment_terms,
                    payment_terms_breakdown:
                      sup.payment_terms_breakdown || r.data.payment_terms_breakdown || null,
                    grand_total: r.data.grand_total };
        } catch (_) { return sup; }
      })).then(updated => {
        setForm(prev => ({...prev, suppliers: updated}));
      });
      return f;
    });
  };

  const addSupplierColumn = () => {
    setForm(f => ({
      ...f,
      suppliers: [...(f.suppliers || []), {
        id: crypto.randomUUID(),
        supplier_id:   '', supplier_name: '',
        vendor_quote_id: '', quote_no: '',
        payment_terms: '', payment_terms_breakdown: null, valid_until: '',
        rows: [], grand_total: 0,
        covered_items: 0, total_items: f.items.length,
        vendor_quotes: [],   // local cache of this supplier's VQ list
        notes: '',
      }],
    }));
  };
  const removeSupplierColumn = (id) => {
    setForm(f => ({...f, suppliers: (f.suppliers || []).filter(s => s.id !== id)}));
  };
  const updateSupplierColumn = (id, patch) => {
    setForm(f => ({
      ...f,
      suppliers: (f.suppliers || []).map(s => s.id === id ? {...s, ...patch} : s),
    }));
  };

  const onSupplierPick = async (colId, supplierId) => {
    const sup = suppliers.find(s => s.id === supplierId);
    updateSupplierColumn(colId, {
      supplier_id: supplierId,
      supplier_name: sup?.name_english || sup?.name_arabic || '',
      vendor_quote_id: '', quote_no: '',
      rows: [], grand_total: 0, vendor_quotes: [],
    });
    if (!supplierId) return;
    try {
      const r = await axiosInstance.get(`/price-comparisons/meta/vendor-quotes?supplier_id=${supplierId}`);
      updateSupplierColumn(colId, { vendor_quotes: r.data?.vendor_quotes || [] });
    } catch (_) {/* silent */}
  };

  const onQuotePick = async (colId, vqId) => {
    if (!vqId) {
      updateSupplierColumn(colId, { vendor_quote_id: '', quote_no: '', rows: [], grand_total: 0 });
      return;
    }
    try {
      const r = await axiosInstance.post('/price-comparisons/autofill-supplier-column', {
        vendor_quote_id: vqId, items: form.items,
      });
      updateSupplierColumn(colId, {
        vendor_quote_id: r.data.vendor_quote_id,
        quote_no:        r.data.quote_no,
        payment_terms:   r.data.payment_terms,
        payment_terms_breakdown: r.data.payment_terms_breakdown || null,
        valid_until:     r.data.valid_until,
        rows:            r.data.rows,
        grand_total:     r.data.grand_total,
        covered_items:   r.data.covered_items,
        total_items:     r.data.total_items,
      });
      toast.success(`Loaded ${r.data.quote_no} · ${r.data.covered_items}/${r.data.total_items} items covered`);
    } catch (_) { toast.error('Autofill failed'); }
  };

  const submit = async (statusToSet) => {
    if (statusToSet === 'saved' && form.items.length === 0) {
      toast.error('Pick items from Customer RFQ before saving'); return;
    }
    setBusy(true);
    try {
      // Strip local-only state (vendor_quotes list) before persisting.
      const payload = {
        ...form,
        status: statusToSet,
        suppliers: (form.suppliers || []).map(({vendor_quotes, ...rest}) => rest),
      };
      if (existing && existing.id) {
        await axiosInstance.patch(`/price-comparisons/${existing.id}`, payload);
        toast.success(`Updated (${statusToSet})`);
      } else {
        const r = await axiosInstance.post('/price-comparisons', payload);
        toast.success(`${r.data?.ref_no} ${statusToSet === 'saved' ? 'saved' : 'drafted'}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="price-comparison-form-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[94vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold inline-flex items-center gap-2">
            <Scale className="w-5 h-5" />
            {existing && existing.id ? `Edit · ${existing.ref_no}` :
              existing && existing.revised_from_ref ? `Revise · cloned from ${existing.revised_from_ref}` :
              'New Comparison'}
          </h2>
          <button onClick={onClose} data-testid="price-comparison-form-close"
                  className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div>
              <label className="text-xs font-semibold text-gray-700">Date</label>
              <input type="date" value={form.date}
                      onChange={(e) => setForm(f => ({...f, date: e.target.value}))}
                      data-testid="price-comparison-form-date"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">
                Customer <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select value={form.customer_id}
                       onChange={(e) => setForm(f => ({...f, customer_id: e.target.value}))}
                       data-testid="price-comparison-form-customer"
                       className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white">
                <option value="">— any —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name_english || c.name_arabic || c.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">
                Project <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select value={form.project_id}
                       onChange={(e) => setForm(f => ({...f, project_id: e.target.value}))}
                       data-testid="price-comparison-form-project"
                       className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white">
                <option value="">— any —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.project_code ? `${p.project_code} · ` : ''}{p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Type of Work</label>
              <select value={form.type_of_work}
                       onChange={(e) => setForm(f => ({...f, type_of_work: e.target.value}))}
                       data-testid="price-comparison-form-tow"
                       className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white">
                <option value="">— select —</option>
                <option value="Supply">Supply</option>
                <option value="Installation">Installation</option>
                <option value="Supply & Installation">Supply &amp; Installation</option>
              </select>
            </div>
          </div>

          {/* Source RFQ picker */}
          <RfqSourcePanel
            availableRfqs={availableRfqs}
            alreadyImported={form.items.map(it => `${it.source_rfq_id}:${it.source_line_no}`)}
            onImport={importLinesFromRfq} />

          {/* Items table (Table 1) */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-200 text-sm font-semibold text-indigo-900 flex items-center justify-between">
              <span>📋 Items to Compare · <span className="text-gray-600 font-normal">{form.items.length} line(s)</span></span>
            </div>
            {form.items.length === 0 ? (
              <p className="text-center text-gray-400 py-6 text-xs" data-testid="price-comparison-items-empty">
                Pick items from a Customer RFQ above to populate this table.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] text-gray-600 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-2 py-1.5 w-8">#</th>
                    <th className="text-left px-2 py-1.5 w-20">Category</th>
                    <th className="text-left px-2 py-1.5 w-32">Type of Work</th>
                    <th className="text-left px-2 py-1.5">Description</th>
                    <th className="text-left px-2 py-1.5 w-28">Size</th>
                    <th className="text-left px-2 py-1.5 w-16">Unit</th>
                    <th className="text-right px-2 py-1.5 w-16">Qty</th>
                    <th className="text-left px-2 py-1.5 w-28 text-[10px]">Source</th>
                    <th className="px-1 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it) => (
                    <tr key={it.no} className="border-t border-gray-100 align-top"
                        data-testid={`price-comparison-item-row-${it.no}`}>
                      <td className="px-2 py-1.5 font-mono text-xs text-gray-500">{it.no}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{formatCategory(it.category)}</td>
                      <td className="px-2 py-1.5 text-xs">{it.type_of_work || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-pre-wrap leading-snug text-xs">{it.description}</td>
                      <td className="px-2 py-1.5 whitespace-pre-wrap text-xs">{it.size || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{it.unit || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-xs text-right">{it.qty}</td>
                      <td className="px-2 py-1.5 text-[10px] font-mono text-gray-500">
                        {it.source_rfq_no || '—'}
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        <button onClick={() => removeItem(it.no)}
                                data-testid={`price-comparison-item-drop-${it.no}`}
                                className="text-rose-500 hover:text-rose-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Supplier columns (Table 2, 3, ...) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 inline-flex items-center gap-2">
                <Scale className="w-4 h-4" /> Supplier Quotes ({(form.suppliers || []).length})
              </h3>
              <button onClick={addSupplierColumn}
                      data-testid="price-comparison-add-supplier"
                      disabled={form.items.length === 0}
                      className="px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                <Plus className="w-3 h-3" /> Add Supplier
              </button>
            </div>
            {(form.suppliers || []).length === 0 && form.items.length > 0 && (
              <p className="text-center text-gray-400 py-4 text-xs border border-dashed border-gray-300 rounded"
                 data-testid="price-comparison-suppliers-empty">
                Click <strong>+ Add Supplier</strong> to create a comparison column.
              </p>
            )}
            {(form.suppliers || []).map((col, idx) => (
              <SupplierColumn key={col.id}
                               index={idx + 1}
                               col={col}
                               suppliers={suppliers}
                               onPickSupplier={(sid) => onSupplierPick(col.id, sid)}
                               onPickQuote={(vqId) => onQuotePick(col.id, vqId)}
                               onUpdate={(patch) => updateSupplierColumn(col.id, patch)}
                               onRemove={() => removeSupplierColumn(col.id)} />
            ))}
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-gray-700">Notes</label>
            <textarea rows={2} value={form.notes}
                       onChange={(e) => setForm(f => ({...f, notes: e.target.value}))}
                       data-testid="price-comparison-form-notes"
                       className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-y" />
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-between gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="price-comparison-form-cancel"
                  className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold">
            Cancel
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => submit('draft')} disabled={busy}
                    data-testid="price-comparison-form-save-draft"
                    className="px-3 py-1.5 rounded border border-amber-400 text-amber-700 hover:bg-amber-50 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
              <Save className="w-4 h-4" /> Save as Draft
            </button>
            <button onClick={() => submit('saved')} disabled={busy}
                    data-testid="price-comparison-form-save"
                    className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
              <CheckCircle className="w-4 h-4" /> Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ───────────────────── Source RFQ picker ─────────────────────
const RfqSourcePanel = ({ availableRfqs, alreadyImported, onImport }) => {
  const [expanded, setExpanded] = useState(null);   // id of expanded RFQ
  const [selectedLines, setSelectedLines] = useState({}); // {rfq_id: [line_keys]}

  if (availableRfqs.length === 0) return (
    <div className="border border-dashed border-amber-300 bg-amber-50/40 rounded-lg p-3 text-xs text-amber-900 inline-flex items-center gap-2"
         data-testid="price-comparison-no-rfqs">
      <AlertCircle className="w-4 h-4" /> No Customer RFQs found for the selected Customer / Project.
      <a href="/customer-rfqs" className="text-amber-700 underline ml-2">Create one →</a>
    </div>
  );

  const toggleLine = (rfqId, key) => {
    setSelectedLines(prev => {
      const cur = prev[rfqId] || [];
      return {...prev, [rfqId]: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]};
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="price-comparison-source-rfqs">
      <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 font-semibold text-sm text-sky-900 flex items-center justify-between gap-2 flex-wrap">
        <span>🔎 Available Customer RFQs ({availableRfqs.length})</span>
        <button onClick={() => {
                  // Bulk import every still-unselected line from every RFQ.
                  availableRfqs.forEach((rfq) => {
                    const keys = (rfq.lines || [])
                      .map((ln) => `${rfq.id}:${ln.no}`)
                      .filter((k) => !alreadyImported.includes(k));
                    if (keys.length > 0) onImport(rfq, keys);
                  });
                  setSelectedLines({});
                }}
                data-testid="price-comparison-select-all-rfqs"
                className="px-2 py-1 rounded bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold inline-flex items-center gap-1">
          ✓ Select All & Import
        </button>
      </div>
      <div className="divide-y divide-gray-100">
        {availableRfqs.map(rfq => {
          const isOpen = expanded === rfq.id;
          const picked = selectedLines[rfq.id] || [];
          return (
            <div key={rfq.id} data-testid={`price-comparison-source-rfq-${rfq.rfq_no}`}>
              <button onClick={() => setExpanded(isOpen ? null : rfq.id)}
                       className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-sm">
                <span className="inline-flex items-center gap-2">
                  <span className="font-mono text-xs text-indigo-700">{rfq.rfq_no}</span>
                  <span className="text-gray-700">{rfq.customer_name || '—'}</span>
                  <span className="text-xs text-gray-500">· {rfq.project_name || 'no project'}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 font-mono">{rfq.status}</span>
                </span>
                <span className="text-xs text-gray-400">{(rfq.lines || []).length} line(s) · {isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div className="bg-gray-50/60 px-3 py-2 space-y-2">
                  <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="text-left px-1 py-1 w-6">
                          {(() => {
                            const allKeys = (rfq.lines || [])
                              .map((ln) => `${rfq.id}:${ln.no}`)
                              .filter((k) => !alreadyImported.includes(k));
                            const allChecked = allKeys.length > 0 && allKeys.every((k) => picked.includes(k));
                            return (
                              <input type="checkbox"
                                      checked={allChecked}
                                      title={allChecked ? 'Deselect all' : 'Select all lines'}
                                      onChange={() => {
                                        setSelectedLines((prev) => ({
                                          ...prev,
                                          [rfq.id]: allChecked ? [] : allKeys,
                                        }));
                                      }}
                                      data-testid={`price-comparison-select-all-${rfq.rfq_no}`} />
                            );
                          })()}
                        </th>
                        <th className="text-left px-1 py-1 w-6">#</th>
                        <th className="text-left px-1 py-1 w-16">Cat</th>
                        <th className="text-left px-1 py-1">Description</th>
                        <th className="text-left px-1 py-1 w-20">Size</th>
                        <th className="text-left px-1 py-1 w-12">Unit</th>
                        <th className="text-right px-1 py-1 w-16">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rfq.lines || []).map(ln => {
                        const key = `${rfq.id}:${ln.no}`;
                        const isImported = alreadyImported.includes(key);
                        return (
                          <tr key={key} className={isImported ? 'opacity-40' : ''}
                              data-testid={`price-comparison-source-line-${rfq.rfq_no}-${ln.no}`}>
                            <td className="px-1 py-1">
                              <input type="checkbox" disabled={isImported}
                                      checked={picked.includes(key)}
                                      onChange={() => toggleLine(rfq.id, key)}
                                      data-testid={`price-comparison-line-check-${rfq.rfq_no}-${ln.no}`} />
                            </td>
                            <td className="px-1 py-1 font-mono">{ln.no}</td>
                            <td className="px-1 py-1 font-mono">{formatCategory(ln.category)}</td>
                            <td className="px-1 py-1 whitespace-pre-wrap leading-snug">{ln.description}</td>
                            <td className="px-1 py-1 whitespace-pre-wrap">{ln.size || '—'}</td>
                            <td className="px-1 py-1 font-mono">{ln.unit || '—'}</td>
                            <td className="px-1 py-1 font-mono text-right">{ln.qty}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex justify-end">
                    <button disabled={picked.length === 0}
                            onClick={() => { onImport(rfq, picked); setSelectedLines(p => ({...p, [rfq.id]: []})); }}
                            data-testid={`price-comparison-import-${rfq.rfq_no}`}
                            className="px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-40">
                      Import {picked.length} line(s) →
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ───────────────────── Supplier column ───────────────────────
const SupplierColumn = ({ index, col, suppliers, onPickSupplier, onPickQuote,
                          onUpdate, onRemove }) => {
  const coverageTone = col.total_items === 0
    ? 'bg-gray-100 text-gray-700'
    : col.covered_items === col.total_items
      ? 'bg-emerald-100 text-emerald-900'
      : col.covered_items > 0
        ? 'bg-amber-100 text-amber-900'
        : 'bg-rose-100 text-rose-800';

  return (
    <div className="border border-violet-200 rounded-lg bg-white overflow-hidden"
         data-testid={`price-comparison-supplier-col-${index}`}>
      <div className="px-3 py-2 bg-violet-50 border-b border-violet-200 flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-violet-900">Supplier #{index}</span>
        <select value={col.supplier_id}
                 onChange={(e) => onPickSupplier(e.target.value)}
                 data-testid={`price-comparison-pick-supplier-${index}`}
                 className="border border-violet-300 rounded px-2 py-1 text-xs bg-white min-w-[180px]">
          <option value="">— select supplier —</option>
          {suppliers.map(s => (
            <option key={s.id} value={s.id}>{s.name_english || s.name_arabic || s.id}</option>
          ))}
        </select>
        <select value={col.vendor_quote_id || ''}
                 onChange={(e) => onPickQuote(e.target.value)}
                 disabled={!col.supplier_id}
                 data-testid={`price-comparison-pick-vq-${index}`}
                 className="border border-violet-300 rounded px-2 py-1 text-xs bg-white min-w-[180px] disabled:opacity-50">
          <option value="">— select Vendor Quotation —</option>
          {(col.vendor_quotes || []).map(vq => (
            <option key={vq.id} value={vq.id}>
              {vq.quote_no} · {vq.quote_date}{vq.valid_until ? ` · valid ${vq.valid_until}` : ''}
            </option>
          ))}
        </select>
        {col.vendor_quote_id && (
          <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${coverageTone}`}>
            {col.covered_items}/{col.total_items} covered
          </span>
        )}
        <button onClick={onRemove} data-testid={`price-comparison-remove-supplier-${index}`}
                 className="ml-auto text-rose-500 hover:text-rose-700">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {/* Payment Terms — 7-bucket breakdown (auto-filled from picked VQ). */}
      <div className="px-3 py-2 bg-violet-50/40 border-b border-violet-100">
        {(() => {
          const bd     = col.payment_terms_breakdown || blankBreakdown();
          const total  = PT_FIELDS.reduce((s, f) => s + (Number(bd[f.key]) || 0), 0);
          const isValid = total === 100;
          const update = (key, val) => {
            const v = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
            const next = { ...bd, [key]: v === '' ? '' : v };
            onUpdate({
              payment_terms_breakdown: next,
              payment_terms: summarizeBreakdown(next),
            });
          };
          const toneCls = total === 0
            ? 'bg-gray-200 text-gray-600'
            : isValid
              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
              : 'bg-rose-100 text-rose-800 border border-rose-300';
          return (
            <>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <label className="text-xs text-violet-900 font-bold inline-flex items-center gap-1">
                  💳 Payment Terms — Breakdown (%)
                </label>
                <span data-testid={`price-comparison-pt-total-${index}`}
                      className={`text-[10px] px-2 py-0.5 rounded font-semibold font-mono ${toneCls}`}>
                  Total: {total}%
                  {total > 0 && !isValid && (total < 100 ? ` · ${100 - total}% rem.` : ` · over by ${total - 100}%`)}
                </span>
                <span className="text-[10px] text-gray-500">
                  Auto-filled from supplier quotation — edit any bucket.
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-1">
                {PT_FIELDS.map((f) => (
                  <div key={f.key} className="flex items-center gap-1.5">
                    <label htmlFor={`pt-${index}-${f.key}`}
                            className="text-[11px] text-gray-700 flex-1 leading-tight">{f.label}</label>
                    <div className="relative">
                      <input id={`pt-${index}-${f.key}`} type="number" step="0.01" min="0" max="100"
                              value={bd[f.key]}
                              onChange={(e) => update(f.key, e.target.value)}
                              data-testid={`price-comparison-pt-${index}-${f.key}`}
                              className="w-16 text-[11px] text-right border border-gray-300 rounded px-1 py-1 pr-4 font-mono" />
                      <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] text-gray-400 pointer-events-none">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>
      {(col.rows || []).length === 0 ? (
        <p className="px-3 py-3 text-xs text-gray-400 text-center">
          Pick a supplier + Vendor Quotation to auto-fill prices.
        </p>
      ) : (
        <table className="w-full text-sm">
          {(() => {
            const hasInstall = (col.rows || []).some(
              (r) => r.type_of_work === 'Supply & Installation',
            );
            return (
          <>
          <thead className="bg-gray-50/60 text-[11px] text-gray-600 uppercase tracking-wider">
            <tr>
              <th className="text-left px-2 py-1.5 w-8">#</th>
              <th className="text-left px-2 py-1.5 w-28">Type of Work</th>
              <th className="text-left px-2 py-1.5">Description</th>
              <th className="text-left px-2 py-1.5">Supplier Description</th>
              <th className="text-right px-2 py-1.5 w-20">Qty</th>
              <th className="text-right px-2 py-1.5 w-24">Unit Price</th>
              {hasInstall && (
                <th className="text-right px-2 py-1.5 w-24">Install Price</th>
              )}
              <th className="text-right px-2 py-1.5 w-24">Total</th>
              <th className="text-right px-2 py-1.5 w-24">Delivery Time</th>
            </tr>
          </thead>
          <tbody>
            {col.rows.map((r) => (
              <tr key={r.item_no} className={`border-t border-gray-100 ${!r.covered ? 'bg-rose-50/40' : ''}`}>
                <td className="px-2 py-1 font-mono text-xs text-gray-500">{r.item_no}</td>
                <td className="px-2 py-1 text-xs"
                    data-testid={`price-comparison-tow-${index}-${r.item_no}`}>
                  {r.type_of_work ? (
                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold text-[10px]">
                      {r.type_of_work}
                    </span>
                  ) : (
                    <span className="text-gray-400 italic">—</span>
                  )}
                </td>
                <td className="px-2 py-1 text-xs whitespace-pre-wrap">
                  {r.description}
                  {(r.brand_match && r.brand_match !== 'open') && (
                    <span data-testid={`price-comparison-brand-match-${index}-${r.item_no}`}
                          className={`ml-1 inline-block text-[9px] px-1 py-0.5 rounded font-semibold ${
                            r.brand_match === 'match'      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                            r.brand_match === 'alternative'? 'bg-amber-100 text-amber-800 border border-amber-300' :
                            r.brand_match === 'mismatch'   ? 'bg-rose-100 text-rose-800 border border-rose-300' :
                                                             'bg-gray-100 text-gray-600 border border-gray-300'
                          }`}
                          title={`req: ${r.requested_brand || '—'} / offered: ${r.offered_brand || '—'} / ${r.brand_strictness || 'Open'}`}>
                      {r.brand_match === 'match'        ? '✓ brand'    :
                       r.brand_match === 'alternative'  ? '⚠ alt'      :
                       r.brand_match === 'mismatch'     ? '✗ mismatch' :
                                                          '? no brand'}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1 text-xs whitespace-pre-wrap text-gray-700">
                  {r.supplier_description || <span className="text-gray-400 italic">—</span>}
                </td>
                <td className="px-2 py-1 text-xs font-mono text-right">{r.qty}</td>
                <td className="px-2 py-1 text-xs font-mono text-right">
                  {r.covered ? r.unit_price.toFixed(2) :
                    <span className="text-rose-500">no quote</span>}
                </td>
                {hasInstall && (
                  <td className="px-2 py-1 text-xs font-mono text-right"
                      data-testid={`price-comparison-install-price-${index}-${r.item_no}`}>
                    {r.type_of_work === 'Supply & Installation' && r.install_price != null
                      ? Number(r.install_price).toFixed(2)
                      : <span className="text-gray-400 italic">—</span>}
                  </td>
                )}
                <td className="px-2 py-1 text-xs font-mono text-right font-semibold">
                  {r.covered ? r.total.toFixed(2) : '—'}
                </td>
                <td className="px-2 py-1 text-xs font-mono text-right">
                  {r.lead_time_days != null ? `${r.lead_time_days}d` : '—'}
                </td>
              </tr>
            ))}
            <tr className="bg-violet-50 border-t border-violet-200">
              <td colSpan={hasInstall ? 7 : 6} className="px-2 py-1.5 text-xs font-semibold text-right">Grand Total</td>
              <td className="px-2 py-1.5 text-xs font-mono font-bold text-right text-violet-900"
                  data-testid={`price-comparison-grand-total-${index}`}>
                {(col.grand_total || 0).toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tbody>
          </>
            );
          })()}
        </table>
      )}
    </div>
  );
};


// ───────────────────── Detail Modal ──────────────────────────
const ComparisonDetail = ({ comparison, approvalCfg, onClose, onEdit, onRefresh }) => {
  const winner = useMemo(() => {
    const eligible = (comparison.suppliers || [])
      .filter(s => s.vendor_quote_id && s.covered_items === s.total_items && s.total_items > 0);
    if (eligible.length === 0) return null;
    return eligible.reduce((best, s) =>
      !best || s.grand_total < best.grand_total ? s : best, null);
  }, [comparison]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="price-comparison-detail-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[94vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold inline-flex items-center gap-2">
            <Scale className="w-5 h-5" />
            <span className="font-mono text-indigo-700">{comparison.ref_no}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[comparison.status] || STATUS_TONE.draft}`}
                  data-testid="price-comparison-detail-status">
              {comparison.status}
            </span>
            {winner && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-900 font-semibold">
                🏆 Best Total: {winner.supplier_name} · {winner.grand_total.toFixed(2)}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            {comparison.status === 'draft' && (
              <button onClick={onEdit}
                       className="text-xs px-2 py-1 rounded border border-amber-400 text-amber-700 hover:bg-amber-50 font-semibold">
                Edit Draft
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Award & Approval Pipeline — Feb 2026.
              Sits at the top of the detail so the user knows where in the
              pipeline this comparison is and what their next action is. */}
          <AwardApprovalPanel comparison={comparison}
                               approvalCfg={approvalCfg}
                               winner={winner}
                               onRefresh={onRefresh} />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <Field label="Date" value={comparison.date} />
            <Field label="Customer" value={comparison.customer_name} />
            <Field label="Project" value={comparison.project_name} />
            <Field label="Type of Work" value={comparison.type_of_work} />
          </div>
          {/* Best by Criteria — quick verdict on Price, Delivery and Payment Terms. */}
          <BestByCriteria comparison={comparison} />
          {/* Items */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Unified Comparison · {(comparison.items || []).length} item(s) × {(comparison.suppliers || []).length} supplier(s)
            </div>
            <div className="overflow-x-auto">
              <UnifiedComparisonTable comparison={comparison} winner={winner} />
            </div>
          </div>
          {/* Payment Terms — per-supplier breakdown summary. */}
          {(comparison.suppliers || []).some(
            (s) => (s.payment_terms_breakdown
                    && Object.values(s.payment_terms_breakdown).some((v) => Number(v) > 0))
                  || s.payment_terms
          ) && (
            <div className="border border-violet-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-violet-50 border-b border-violet-200 text-sm font-semibold text-violet-900">
                💳 Payment Terms — by Supplier
              </div>
              <div className="divide-y divide-violet-100">
                {(comparison.suppliers || []).map((s, idx) => {
                  const bd = s.payment_terms_breakdown || {};
                  const buckets = PT_FIELDS.filter((f) => Number(bd[f.key]) > 0);
                  const hasNothing = buckets.length === 0 && !s.payment_terms;
                  if (hasNothing) return null;
                  const isWinner = winner && s.id === winner.id;
                  return (
                    <div key={s.id || idx}
                          data-testid={`price-comparison-detail-pt-${idx}`}
                          className={`px-3 py-2 ${isWinner ? 'bg-emerald-50/60' : ''}`}>
                      <div className="text-xs font-semibold text-gray-800 mb-1.5 inline-flex items-center gap-2 flex-wrap">
                        {s.supplier_name || `Supplier #${idx + 1}`}
                        {s.quote_no && <span className="font-mono text-[10px] text-gray-500">· {s.quote_no}</span>}
                        {isWinner && <span className="text-[9px] bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded">🏆 Best Total</span>}
                      </div>
                      {buckets.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {buckets.map((f) => (
                            <span key={f.key}
                                  className="text-[11px] px-2 py-0.5 rounded border border-violet-200 bg-violet-50 text-violet-900">
                              {f.label}: <strong className="font-mono">{Number(bd[f.key])}%</strong>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-600 whitespace-pre-wrap">{s.payment_terms}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {comparison.notes && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Notes</p>
              <p className="text-sm bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap">{comparison.notes}</p>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} data-testid="price-comparison-detail-close"
                  className="px-3 py-1.5 rounded bg-gray-900 text-white text-sm font-semibold">Close</button>
        </div>
      </div>
    </div>
  );
};


const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-sm font-medium">{value || '—'}</p>
  </div>
);


// ───────────── Award & Approval Pipeline Panel ─────────────────
// Status-aware panel sitting at the top of the ComparisonDetail modal.
// Drives the entire workflow per the Feb-2026 user spec:
//   • draft / saved        → "🏆 Select Winner & Propose Pricing" button
//   • awarded              → review pricing + "Submit for Approval"
//   • pending_procurement  → Approve / Reject (if role allows)
//   • pending_finance      → Approve / Reject (if role allows)
//   • approved             → green badge + summary; rep handoff happens
//                            in the Approved-Quotes screen
//   • rejected             → red banner with reason
const AwardApprovalPanel = ({ comparison, approvalCfg, winner, onRefresh }) => {
  const [showAward, setShowAward] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(null); // 'procurement' | 'finance' | 'sales' | null
  const [rejectNotes, setRejectNotes] = useState('');
  // Sales review — per-line edits buffer keyed by item_no.
  const [salesEdits, setSalesEdits] = useState({});
  // Send-to-customer modal state.
  const [showSendCustomer, setShowSendCustomer] = useState(false);

  const status = comparison.status;
  const award = comparison.award || {};
  const pricing = comparison.pricing || {};
  const approval = comparison.approval || { procurement: {}, finance: {}, sales: {}, history: [] };
  const sendLog = comparison.send_log || {};
  const canProc  = approvalCfg?.can_approve?.procurement;
  const canFin   = approvalCfg?.can_approve?.finance;
  const canSales = approvalCfg?.can_approve?.sales;

  const awardedSupplier = (comparison.suppliers || []).find(
    (s) => s.id === award.awarded_supplier_id,
  );

  const submit = async () => {
    setBusy(true);
    try {
      await axiosInstance.post(`/price-comparisons/${comparison.id}/submit`);
      toast.success('Submitted for approval');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Submission failed');
    } finally { setBusy(false); }
  };

  const approve = async (step) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/price-comparisons/${comparison.id}/approve`, { step });
      toast.success(`${step} approved`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.response?.data?.detail?.message || 'Approval failed');
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    setBusy(true);
    try {
      await axiosInstance.post(`/price-comparisons/${comparison.id}/reject`,
                                { notes: rejectNotes });
      toast.success('Comparison rejected');
      setRejectMode(null); setRejectNotes('');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Reject failed');
    } finally { setBusy(false); }
  };

  const startSalesReview = async () => {
    setBusy(true);
    try {
      // Comparison module stops at finance approval (Feb 2026 split).
      // Spin up a dedicated Sales Quotation document instead of an
      // in-place review step. Lands the user on /sales-quotations.
      const r = await axiosInstance.post(`/sales-quotations/from-comparison/${comparison.id}`);
      toast.success(`Sales Quotation ${r.data?.quote_no} created`);
      window.location.assign('/sales-quotations');
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Could not create Sales Quotation');
    } finally { setBusy(false); }
  };

  const saveSalesDescriptions = async () => {
    const lines = Object.entries(salesEdits)
      .filter(([, v]) => v && v.trim())
      .map(([k, v]) => ({ item_no: Number(k), sales_description: v.trim() }));
    if (lines.length === 0) return;
    setBusy(true);
    try {
      await axiosInstance.patch(`/price-comparisons/${comparison.id}/sales-descriptions`,
                                  { lines });
      toast.success(`Saved ${lines.length} description(s)`);
      setSalesEdits({});
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Save failed');
    } finally { setBusy(false); }
  };

  const sendToEmployee = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/price-comparisons/${comparison.id}/send-to-employee`, {});
      const emp = r.data?.send_log?.employee || {};
      if (emp.delivered) {
        toast.success(`📧 Sent to ${emp.to} (CC: ${(emp.cc || []).join(', ')})`);
      } else {
        toast.success(`Status updated. Email service unavailable — please re-send from logs (to=${emp.to})`);
      }
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Send failed');
    } finally { setBusy(false); }
  };

  // ──── State: draft / saved → entry point to the pipeline ────
  if (status === 'draft' || status === 'saved') {
    return (
      <div className="border-2 border-dashed border-indigo-200 bg-indigo-50/40 rounded-lg p-4"
            data-testid="award-panel-entry">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold text-indigo-900 inline-flex items-center gap-2">
              🏆 Award Pipeline · ابدأ اعتماد المورد الفائز
            </h3>
            <p className="text-xs text-gray-700 mt-1">
              اختر المورد الذي ستتعامل معه، حدّد هامش الربح، وأرسل القرار للاعتماد عبر مسارين (Procurement → Finance).
            </p>
          </div>
          <button onClick={() => setShowAward(true)} disabled={(comparison.suppliers || []).length === 0}
                  data-testid="award-panel-open-button"
                  className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white text-sm font-semibold">
            Select Winner & Propose Pricing →
          </button>
        </div>
        {showAward && (
          <AwardModal comparison={comparison} winner={winner}
                       onClose={() => setShowAward(false)}
                       onAwarded={() => { setShowAward(false); onRefresh && onRefresh(); }} />
        )}
      </div>
    );
  }

  // ──── State: rejected → red banner, allow re-award ────
  if (status === 'rejected') {
    const lastEvt = [...(approval.history || [])].reverse().find((h) => h.action === 'rejected');
    return (
      <div className="border-l-4 border-rose-500 bg-rose-50 rounded-lg p-3" data-testid="award-panel-rejected">
        <div className="text-sm font-bold text-rose-900 inline-flex items-center gap-2">
          ❌ Rejected at {lastEvt?.step || '—'} step
        </div>
        {lastEvt && (
          <div className="text-xs text-rose-800 mt-1">
            By <strong>{lastEvt.user_name || '—'}</strong>
            {lastEvt.notes ? <> · &ldquo;{lastEvt.notes}&rdquo;</> : null}
            {lastEvt.at ? <span className="ml-2 text-rose-600">{new Date(lastEvt.at).toLocaleString()}</span> : null}
          </div>
        )}
      </div>
    );
  }

  // ──── States: awarded / pending_* / approved / sales_* / sent_* → full panel ────
  const inSalesReview = status === 'pending_sales_review';
  return (
    <div className="border border-indigo-200 rounded-lg overflow-hidden" data-testid="award-panel">
      {/* Header: award summary */}
      <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-bold text-indigo-900 inline-flex items-center gap-2">
            🏆 Awarded to: <span className="font-mono">{awardedSupplier?.supplier_name || '—'}</span>
          </div>
          {award.reason && (
            <div className="text-xs text-indigo-700 mt-0.5">Reason: {award.reason}</div>
          )}
          <div className="text-[11px] text-indigo-600 mt-0.5">
            By <strong>{award.awarded_by_name}</strong>
            {award.awarded_at && <> · {new Date(award.awarded_at).toLocaleString()}</>}
          </div>
        </div>
        <ApprovalStepper status={status} approval={approval} />
      </div>

      {/* Pricing summary — editable sales_description when in pending_sales_review */}
      {pricing.lines && pricing.lines.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center justify-between">
            <span>💰 Customer Pricing · {pricing.lines.length} line(s) · Markup default {pricing.default_markup_percent}%</span>
            {inSalesReview && (
              <span className="text-[11px] text-sky-700 font-semibold">
                ✏️ Sales Review — edit descriptions then &ldquo;Submit for Sales Approval&rdquo;
              </span>
            )}
          </div>
          <table className="w-full text-xs" data-testid="award-panel-pricing-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-2 py-1">#</th>
                <th className="text-left px-2 py-1" style={{minWidth: '220px'}}>
                  {inSalesReview ? 'Sales Description (editable)' : 'Description'}
                </th>
                <th className="text-right px-2 py-1">Qty</th>
                <th className="text-right px-2 py-1">Supplier Cost</th>
                <th className="text-right px-2 py-1">Markup %</th>
                <th className="text-right px-2 py-1">Customer Price</th>
                <th className="text-right px-2 py-1">Margin</th>
              </tr>
            </thead>
            <tbody>
              {pricing.lines.map((ln, i) => {
                const key = ln.item_no ?? i;
                const currentSD = salesEdits[key] !== undefined
                                   ? salesEdits[key]
                                   : (ln.sales_description || ln.description || '');
                return (
                  <tr key={i} className="border-t border-gray-100 align-top">
                    <td className="px-2 py-1 text-gray-400">{ln.item_no ?? i + 1}</td>
                    <td className="px-2 py-1">
                      {inSalesReview ? (
                        <div>
                          <textarea rows={2} value={currentSD}
                                     onChange={(e) => setSalesEdits((o) => ({ ...o, [key]: e.target.value }))}
                                     data-testid={`sales-desc-input-${ln.item_no ?? i}`}
                                     className="w-full text-xs border border-sky-300 bg-sky-50/40 rounded px-1.5 py-1" />
                          {ln.description !== currentSD && (
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              Supplier: <em>{ln.description}</em>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div>{ln.sales_description || ln.description || '—'}</div>
                          {ln.sales_description && ln.sales_description !== ln.description && (
                            <div className="text-[10px] text-gray-400 italic mt-0.5">
                              Supplier: {ln.description}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{ln.qty}</td>
                    <td className="px-2 py-1 text-right font-mono text-rose-700">{(ln.supplier_total || 0).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right font-mono">{ln.markup_percent}%</td>
                    <td className="px-2 py-1 text-right font-mono text-emerald-700 font-semibold">{(ln.customer_total || 0).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right font-mono">{(ln.margin_amount || 0).toFixed(2)} ({ln.margin_percent}%)</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-gray-300 font-semibold bg-gray-50">
                <td colSpan={3} className="px-2 py-1 text-right text-gray-700">TOTALS</td>
                <td className="px-2 py-1 text-right font-mono text-rose-700">{(pricing.subtotal_supplier_cost || 0).toFixed(2)}</td>
                <td className="px-2 py-1"></td>
                <td className="px-2 py-1 text-right font-mono text-emerald-800">{(pricing.subtotal_customer_price || 0).toFixed(2)}</td>
                <td className="px-2 py-1 text-right font-mono text-indigo-800">{(pricing.total_margin || 0).toFixed(2)} ({pricing.total_margin_percent}%)</td>
              </tr>
            </tbody>
          </table>
          {inSalesReview && Object.keys(salesEdits).length > 0 && (
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setSalesEdits({})}
                      className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
                Discard
              </button>
              <button onClick={saveSalesDescriptions} disabled={busy}
                      data-testid="award-panel-save-sales-desc"
                      className="text-xs px-2 py-1 rounded bg-sky-600 hover:bg-sky-700 text-white font-semibold">
                Save Descriptions
              </button>
            </div>
          )}
        </div>
      )}

      {/* Footer actions — status-aware */}
      <div className="px-4 py-3 bg-gray-50 flex items-center justify-end gap-2 flex-wrap">
        {status === 'awarded' && (
          <>
            <button onClick={() => setShowAward(true)} disabled={busy}
                    data-testid="award-panel-edit-pricing"
                    className="text-xs px-3 py-1.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 font-semibold">
              ✏️ Edit Pricing
            </button>
            <button onClick={submit} disabled={busy}
                    data-testid="award-panel-submit"
                    className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
              Submit for Approval →
            </button>
          </>
        )}
        {status === 'pending_procurement' && (
          <>
            <button onClick={() => setRejectMode('procurement')} disabled={busy || !canProc}
                    data-testid="award-panel-reject-procurement"
                    className="text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold">
              ✗ Reject
            </button>
            <button onClick={() => approve('procurement')} disabled={busy || !canProc}
                    data-testid="award-panel-approve-procurement"
                    title={canProc ? '' : `Allowed roles: ${(approvalCfg?.steps?.procurement || []).join(', ')}`}
                    className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold">
              ✓ Approve as Procurement
            </button>
          </>
        )}
        {status === 'pending_finance' && (
          <>
            <button onClick={() => setRejectMode('finance')} disabled={busy || !canFin}
                    data-testid="award-panel-reject-finance"
                    className="text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold">
              ✗ Reject
            </button>
            <button onClick={() => approve('finance')} disabled={busy || !canFin}
                    data-testid="award-panel-approve-finance"
                    title={canFin ? '' : `Allowed roles: ${(approvalCfg?.steps?.finance || []).join(', ')}`}
                    className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold">
              ✓ Approve as Finance
            </button>
          </>
        )}
        {status === 'approved' && (
          <span className="text-xs text-emerald-800 font-semibold mr-auto inline-flex items-center gap-1.5"
                 data-testid="award-panel-approved-banner">
            <CheckCircle className="w-4 h-4" />
            Procurement + Finance Approved — جاهز للتحويل تلقائياً إلى صفحة عروض السعر
          </span>
        )}
        {status === 'pending_sales_review' && (
          <>
            <button onClick={() => setRejectMode('sales')} disabled={busy || !canSales}
                    data-testid="award-panel-reject-sales"
                    className="text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold">
              ✗ Reject
            </button>
            <button onClick={() => approve('sales')} disabled={busy || !canSales}
                    data-testid="award-panel-approve-sales"
                    title={canSales ? '' : `Allowed roles: ${(approvalCfg?.steps?.sales || []).join(', ')}`}
                    className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold">
              ✓ Submit for Sales Approval
            </button>
          </>
        )}
        {status === 'sales_approved' && (
          <>
            <span className="text-xs text-teal-800 font-semibold mr-auto inline-flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4" /> Sales Approved — ready to send to employee
            </span>
            <button onClick={sendToEmployee} disabled={busy}
                    data-testid="award-panel-send-to-employee"
                    className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
              📧 Send to Employee (CC: aghiad)
            </button>
          </>
        )}
        {status === 'sent_to_employee' && (
          <>
            <span className="text-xs text-indigo-800 font-semibold mr-auto"
                   data-testid="award-panel-sent-to-employee-banner">
              📨 Sent to <strong>{sendLog.employee?.to}</strong>
              {sendLog.employee?.sent_at && <> · {new Date(sendLog.employee.sent_at).toLocaleString()}</>}
              {sendLog.employee?.delivered === false && (
                <span className="ml-2 text-amber-700">⚠ Email not delivered ({sendLog.employee?.error || 'no provider'})</span>
              )}
            </span>
            <button onClick={() => setShowSendCustomer(true)} disabled={busy}
                    data-testid="award-panel-send-to-customer"
                    className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              📤 Send to Customer ▾
            </button>
          </>
        )}
        {status === 'sent_to_customer' && (
          <span className="text-xs text-white bg-green-600 px-3 py-1.5 rounded font-semibold inline-flex items-center gap-1.5"
                 data-testid="award-panel-sent-to-customer-banner">
            ✅ Sent to Customer
            {sendLog.customer?.method === 'manual'
              ? <> · <strong>Manual</strong> ({sendLog.customer?.notes || 'external'})</>
              : <> · <strong>Email</strong> to {sendLog.customer?.to}</>}
            {sendLog.customer?.sent_at && <> · {new Date(sendLog.customer.sent_at).toLocaleString()}</>}
          </span>
        )}
      </div>

      {/* Reject inline form */}
      {rejectMode && (
        <div className="px-4 py-3 border-t border-rose-200 bg-rose-50">
          <div className="text-xs font-semibold text-rose-800 mb-1.5">
            Reject at {rejectMode} step — يرجى ذكر السبب
          </div>
          <textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                    rows={2} placeholder="سبب الرفض (إجباري)"
                    data-testid="award-panel-reject-notes"
                    className="w-full text-xs border border-rose-200 rounded p-2 bg-white" />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setRejectMode(null); setRejectNotes(''); }}
                    className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={doReject} disabled={busy || !rejectNotes.trim()}
                    data-testid="award-panel-reject-confirm"
                    className="text-xs px-3 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:bg-gray-300 font-semibold">
              Confirm Reject
            </button>
          </div>
        </div>
      )}

      {/* Audit history */}
      {(approval.history || []).length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 bg-white">
          <div className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
            Audit Trail · سجل القرارات
          </div>
          <ul className="text-xs text-gray-700 space-y-1">
            {approval.history.map((h, i) => (
              <li key={i} className="inline-flex items-center gap-2 flex-wrap">
                <span className="font-mono text-gray-400">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px] uppercase">{h.step}</span>
                <span className="text-gray-900">{h.action}</span>
                <span className="text-gray-500">by</span>
                <strong>{h.user_name || '—'}</strong>
                {h.notes ? <span className="text-gray-500">— &ldquo;{h.notes}&rdquo;</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showAward && (
        <AwardModal comparison={comparison} winner={winner}
                     onClose={() => setShowAward(false)}
                     onAwarded={() => { setShowAward(false); onRefresh && onRefresh(); }} />
      )}
      {showSendCustomer && (
        <SendToCustomerModal comparison={comparison}
                              onClose={() => setShowSendCustomer(false)}
                              onSent={() => { setShowSendCustomer(false); onRefresh && onRefresh(); }} />
      )}
    </div>
  );
};


// ───────────── 3-step approval stepper (Feb 2026) ────────────
const ApprovalStepper = ({ status, approval }) => {
  const procDone  = (approval?.procurement?.status === 'approved');
  const finDone   = (approval?.finance?.status === 'approved');
  const salesDone = (approval?.sales?.status === 'approved');
  const procActive  = status === 'pending_procurement';
  const finActive   = status === 'pending_finance';
  const salesActive = status === 'pending_sales_review';
  return (
    <div className="flex items-center gap-2 text-[11px]" data-testid="approval-stepper">
      <Step label="Procurement" done={procDone} active={procActive}
            person={approval?.procurement?.approved_by_name} />
      <div className="w-4 h-0.5 bg-gray-300" />
      <Step label="Finance" done={finDone} active={finActive}
            person={approval?.finance?.approved_by_name} />
      <div className="w-4 h-0.5 bg-gray-300" />
      <Step label="Sales" done={salesDone} active={salesActive}
            person={approval?.sales?.approved_by_name} />
    </div>
  );
};


// ───────── Send-to-Customer modal — choose method ────────────
// Per user spec: TWO send options
//   1) Email  → MailerSend to customer_email
//   2) Manual → record "Mark as Sent" (rep used WhatsApp / print / external)
const SendToCustomerModal = ({ comparison, onClose, onSent }) => {
  const [method, setMethod] = useState('email');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (method === 'email' && (!customerEmail || !customerEmail.includes('@'))) {
      toast.error('Customer email required'); return;
    }
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/price-comparisons/${comparison.id}/send-to-customer`,
                                          { method, customer_email: customerEmail || null, notes: notes || null });
      const cust = r.data?.send_log?.customer || {};
      if (method === 'manual') {
        toast.success('Marked as sent (manual)');
      } else if (cust.delivered) {
        toast.success(`📧 Email sent to ${cust.to}`);
      } else {
        toast.success(`Status updated. Email service unavailable — please re-send (to=${cust.to})`);
      }
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Send failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          data-testid="send-to-customer-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">📤 Send Approved Quote to Customer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-semibold">Send Method *</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setMethod('email')}
                       data-testid="send-method-email"
                       className={`px-3 py-2 rounded border text-sm font-semibold ${method === 'email' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                📧 Send by Email
              </button>
              <button type="button" onClick={() => setMethod('manual')}
                       data-testid="send-method-manual"
                       className={`px-3 py-2 rounded border text-sm font-semibold ${method === 'manual' ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                ✋ Mark as Sent
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              {method === 'email'
                ? 'النظام يولّد PDF ويرسله إلى العميل تلقائياً عبر MailerSend.'
                : 'استخدم هذا إذا أرسلت العرض يدوياً (WhatsApp / مطبوع / قناة خارجية) — النظام سيسجل فقط.'}
            </p>
          </div>
          {method === 'email' && (
            <div>
              <label className="text-[11px] uppercase text-gray-500 font-semibold">Customer Email *</label>
              <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="customer@example.com"
                      data-testid="send-customer-email-input"
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-semibold">
              {method === 'manual' ? 'How was it sent? (notes)' : 'Optional cover note'}
            </label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                       placeholder={method === 'manual' ? 'مثلاً: عبر WhatsApp رقم 0555...، أو نسخة مطبوعة' : 'Optional message to include with the quote'}
                       data-testid="send-customer-notes"
                       className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="send-to-customer-submit"
                  className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:bg-gray-300">
            {method === 'email' ? '📧 Send Now' : '✓ Mark as Sent'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Step = ({ label, done, active, person }) => {
  const tone = done ? 'bg-emerald-500 text-white' :
               active ? 'bg-amber-400 text-amber-900 animate-pulse' :
                        'bg-gray-200 text-gray-500';
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-xs font-bold ${tone}`}>
        {done ? '✓' : active ? '…' : '·'}
      </span>
      <span className={`font-semibold ${done ? 'text-emerald-700' : active ? 'text-amber-800' : 'text-gray-500'}`}>
        {label}
      </span>
      {person && <span className="text-gray-500 text-[10px]">· {person}</span>}
    </div>
  );
};


// ───────────────── Award Modal — pick supplier + set markup ─────
const AwardModal = ({ comparison, winner, onClose, onAwarded }) => {
  const isEdit = !!(comparison.award && comparison.award.awarded_supplier_id);
  const [supplierId, setSupplierId] = useState(
    comparison.award?.awarded_supplier_id || winner?.id || (comparison.suppliers || [])[0]?.id || '',
  );
  const [reason, setReason] = useState(comparison.award?.reason || '');
  const [defaultMarkup, setDefaultMarkup] = useState(
    comparison.pricing?.default_markup_percent ?? 25,
  );
  // Per-line overrides as a map { item_no: markup_percent }
  const [overrides, setOverrides] = useState(() => {
    const o = {};
    (comparison.pricing?.lines || []).forEach((ln) => {
      if (ln.markup_percent !== undefined && ln.markup_percent !== comparison.pricing.default_markup_percent) {
        o[ln.item_no ?? ln.item_ref] = ln.markup_percent;
      }
    });
    return o;
  });
  const [busy, setBusy] = useState(false);

  const sup = (comparison.suppliers || []).find((s) => s.id === supplierId);
  const rows = sup?.rows || [];

  // Live preview of customer prices for the picked supplier.
  const preview = rows.map((r) => {
    const qty       = Number(r.qty) || 0;
    const unitCost  = (Number(r.unit_price) || 0) + (Number(r.install_price) || 0);
    const key       = r.item_no ?? r.item_ref;
    const mk        = overrides[key] !== undefined && overrides[key] !== ''
                      ? Number(overrides[key]) : Number(defaultMarkup);
    const cUnit     = unitCost * (1 + (mk || 0) / 100);
    return {
      ...r, key,
      _supplier_total: qty * unitCost,
      _markup_percent: mk,
      _customer_total: qty * cUnit,
      _margin:         qty * (cUnit - unitCost),
    };
  });
  const totSup = preview.reduce((a, b) => a + (b._supplier_total || 0), 0);
  const totCus = preview.reduce((a, b) => a + (b._customer_total || 0), 0);

  const submit = async () => {
    if (!supplierId) { toast.error('Pick a supplier first'); return; }
    setBusy(true);
    try {
      const line_overrides = Object.entries(overrides)
        .filter(([, v]) => v !== '' && v !== null && v !== undefined && !isNaN(Number(v)))
        .map(([k, v]) => ({ item_no: isNaN(Number(k)) ? null : Number(k),
                             item_ref: isNaN(Number(k)) ? k : null,
                             markup_percent: Number(v) }));
      if (isEdit) {
        await axiosInstance.patch(`/price-comparisons/${comparison.id}/pricing`, {
          default_markup_percent: Number(defaultMarkup),
          line_overrides,
        });
        toast.success('Pricing updated');
      } else {
        await axiosInstance.post(`/price-comparisons/${comparison.id}/award`, {
          awarded_supplier_id: supplierId,
          reason: reason || null,
          default_markup_percent: Number(defaultMarkup),
          line_overrides,
        });
        toast.success('Supplier awarded · pricing proposed');
      }
      onAwarded && onAwarded();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || 'Award failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          data-testid="award-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            🏆 {isEdit ? 'Edit Pricing' : 'Award Winner & Propose Customer Pricing'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-4">
          {/* Supplier picker (read-only when editing pricing post-award) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase text-gray-500 font-semibold">Awarded Supplier *</label>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
                       disabled={isEdit}
                       data-testid="award-modal-supplier"
                       className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1 disabled:bg-gray-50">
                <option value="">— pick supplier —</option>
                {(comparison.suppliers || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.supplier_name}{s.grand_total ? ` · total ${s.grand_total.toFixed(2)}` : ''}
                    {winner && s.id === winner.id ? ' 🏆 (best total)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase text-gray-500 font-semibold">Default Markup %</label>
              <input type="number" step="0.5" value={defaultMarkup}
                      onChange={(e) => setDefaultMarkup(e.target.value)}
                      data-testid="award-modal-default-markup"
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
            </div>
          </div>
          {!isEdit && (
            <div>
              <label className="text-[11px] uppercase text-gray-500 font-semibold">Reason / سبب الاختيار</label>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="مثلاً: أرخص سعر، Brand مطابق، أقصر lead time…"
                      data-testid="award-modal-reason"
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
            </div>
          )}
          {/* Live per-line pricing preview */}
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-2 py-1.5">#</th>
                  <th className="text-left px-2 py-1.5">Description</th>
                  <th className="text-right px-2 py-1.5">Qty</th>
                  <th className="text-right px-2 py-1.5">Supplier Cost</th>
                  <th className="text-right px-2 py-1.5 w-32">Markup % (override)</th>
                  <th className="text-right px-2 py-1.5">Customer Price</th>
                  <th className="text-right px-2 py-1.5">Margin</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-2 py-1.5 text-gray-400">{r.item_no ?? i + 1}</td>
                    <td className="px-2 py-1.5">{r.description || '—'}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.qty}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-rose-700">{r._supplier_total.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" step="0.5"
                              value={overrides[r.key] ?? ''}
                              placeholder={String(defaultMarkup)}
                              onChange={(e) => setOverrides((o) => ({ ...o, [r.key]: e.target.value }))}
                              data-testid={`award-modal-line-${i}-markup`}
                              className="w-20 text-right text-xs border border-gray-300 rounded px-1.5 py-0.5" />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-700 font-semibold">
                      {r._customer_total.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-indigo-800">
                      {r._margin.toFixed(2)} ({r._supplier_total ? ((r._margin / r._supplier_total) * 100).toFixed(1) : 0}%)
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 font-semibold bg-gray-50">
                  <td colSpan={3} className="px-2 py-1.5 text-right">TOTALS</td>
                  <td className="px-2 py-1.5 text-right font-mono text-rose-700">{totSup.toFixed(2)}</td>
                  <td></td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-800">{totCus.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-indigo-800">
                    {(totCus - totSup).toFixed(2)} ({totSup ? (((totCus - totSup) / totSup) * 100).toFixed(1) : 0}%)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-500">
            💡 اترك حقل &ldquo;Markup % (override)&rdquo; فارغاً للسطر = يستخدم الافتراضي. أدخل قيمة لتطبيق هامش مختلف لهذا السطر فقط.
          </p>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !supplierId}
                  data-testid="award-modal-submit"
                  className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:bg-gray-300">
            {isEdit ? 'Save Pricing' : 'Confirm Award'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ───────────── Best by Criteria — Price / Delivery / Payment Terms ─────
const BestByCriteria = ({ comparison }) => {
  const suppliers = comparison.suppliers || [];
  // Eligibility: only fully-covered suppliers compete on Price/Delivery.
  const fullCover = suppliers.filter(
    (s) => (s.covered_items || 0) > 0 && s.covered_items === s.total_items,
  );

  // 1) Best Price — lowest grand_total.
  const bestPrice = fullCover.reduce(
    (best, s) => (best == null || (s.grand_total || 0) < (best.grand_total || 0)) ? s : best,
    null,
  );

  // 2) Best Delivery — lowest average lead_time_days across covered rows.
  const avgLead = (s) => {
    const days = (s.rows || []).map((r) => r.lead_time_days).filter((d) => d != null);
    if (days.length === 0) return null;
    return days.reduce((a, b) => a + b, 0) / days.length;
  };
  const eligibleDelivery = fullCover.filter((s) => avgLead(s) != null);
  const bestDelivery = eligibleDelivery.reduce(
    (best, s) => (best == null || avgLead(s) < avgLead(best)) ? s : best,
    null,
  );

  // 3) Best Payment Terms — buyer-friendly score using the 7-bucket breakdown.
  //    Positive weights for later/deferred payments, negative for upfront ones.
  const PT_WEIGHTS = {
    advance_payment:             -1.0,
    design_engineering:          -0.8,
    before_delivery:             -0.5,
    after_delivery:               0.5,
    after_installation:           0.8,
    after_testing_commissioning:  1.0,
    retention:                    1.2,
  };
  const ptScore = (s) => {
    const bd = s.payment_terms_breakdown || {};
    if (Object.values(bd).every((v) => !Number(v))) return null;
    return Object.entries(PT_WEIGHTS).reduce(
      (sum, [k, w]) => sum + (Number(bd[k]) || 0) * w, 0,
    );
  };
  const eligiblePT = suppliers.filter((s) => ptScore(s) != null);
  const bestPT = eligiblePT.reduce(
    (best, s) => (best == null || ptScore(s) > ptScore(best)) ? s : best,
    null,
  );

  const cards = [
    {
      key: 'price',
      label: '💰 Best Price',
      tone: 'border-emerald-300 bg-emerald-50/60',
      titleTone: 'text-emerald-800',
      winner: bestPrice,
      value: bestPrice ? `${(bestPrice.grand_total || 0).toFixed(2)} ${comparison.currency || 'SAR'}` : null,
      hint: 'Lowest fully-covered total',
    },
    {
      key: 'delivery',
      label: '🚚 Best Delivery Time',
      tone: 'border-sky-300 bg-sky-50/60',
      titleTone: 'text-sky-800',
      winner: bestDelivery,
      value: bestDelivery ? `avg ${avgLead(bestDelivery).toFixed(0)} days` : null,
      hint: 'Lowest average lead time',
    },
    {
      key: 'payment',
      label: '💳 Best Payment Terms',
      tone: 'border-violet-300 bg-violet-50/60',
      titleTone: 'text-violet-800',
      winner: bestPT,
      value: bestPT ? `score ${ptScore(bestPT).toFixed(1)}` : null,
      hint: 'Most buyer-friendly schedule',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3"
         data-testid="price-comparison-best-by-criteria">
      {cards.map((c) => (
        <div key={c.key}
              data-testid={`price-comparison-best-${c.key}`}
              className={`border-2 ${c.tone} rounded-lg p-3 flex flex-col gap-1`}>
          <div className={`text-[11px] font-bold uppercase tracking-wider ${c.titleTone}`}>{c.label}</div>
          {c.winner ? (
            <>
              <div className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5">
                🏆 {c.winner.supplier_name || 'Supplier'}
                {c.winner.quote_no && (
                  <span className="font-mono text-[10px] text-gray-500">· {c.winner.quote_no}</span>
                )}
              </div>
              <div className="text-xs font-mono text-gray-800">{c.value}</div>
              <div className="text-[10px] text-gray-500">{c.hint}</div>
            </>
          ) : (
            <div className="text-xs text-gray-400 italic">No eligible supplier — {c.hint.toLowerCase()}</div>
          )}
        </div>
      ))}
    </div>
  );
};


// ─────────── Unified side-by-side comparison table ───────────
const UnifiedComparisonTable = ({ comparison, winner }) => {
  const suppliers = comparison.suppliers || [];
  const items     = comparison.items     || [];

  // Build per-supplier lookup keyed by item_no for O(1) cell access.
  const bySup = useMemo(() => {
    const out = {};
    suppliers.forEach((s) => {
      const key = s.id || s.supplier_id || s.supplier_name;
      out[key] = {};
      (s.rows || []).forEach((r) => { out[key][r.item_no] = r; });
    });
    return out;
  }, [suppliers]);

  // Per-row best (lowest) unit price.
  const bestByRow = useMemo(() => {
    const out = {};
    items.forEach((it) => {
      let bestIdx = null, bestPrice = null;
      suppliers.forEach((s, i) => {
        const k = s.id || s.supplier_id || s.supplier_name;
        const r = bySup[k]?.[it.no];
        if (r && r.covered && r.unit_price != null) {
          if (bestPrice == null || r.unit_price < bestPrice) {
            bestPrice = r.unit_price;
            bestIdx   = i;
          }
        }
      });
      out[it.no] = bestIdx;
    });
    return out;
  }, [items, suppliers, bySup]);

  if (items.length === 0) {
    return <p className="px-3 py-6 text-xs text-gray-400 text-center">No items to compare.</p>;
  }

  // Determine the supplier-block column layout based on the Type of Work mix.
  // - "both": at least one S&I row OR a mix of Supply + Installation → 4 cols
  // - "install": every row is Installation only → 3 cols (no Unit Price)
  // - "supply": default → 3 cols (no Unit Install)
  const tows = items.map((it) => {
    if (it.type_of_work) return it.type_of_work;
    for (const s of suppliers) {
      const k = s.id || s.supplier_id || s.supplier_name;
      const r = bySup[k]?.[it.no];
      if (r?.type_of_work) return r.type_of_work;
    }
    return null;
  });
  const hasSAndI       = tows.some((t) => t === 'Supply & Installation');
  const hasInstallOnly = tows.some((t) => t === 'Installation');
  const hasSupplyOnly  = tows.some((t) => t === 'Supply' || !t);
  let mode;
  if (hasSAndI || (hasInstallOnly && hasSupplyOnly)) mode = 'both';
  else if (hasInstallOnly)                            mode = 'install';
  else                                                mode = 'supply';
  const subCols  = mode === 'both' ? 4 : 3;

  return (
    <table className="text-xs min-w-full"
           data-testid="price-comparison-unified-table">
      <thead>
        {/* Top header row: supplier names spanning sub-columns dynamically. */}
        <tr className="bg-indigo-700 text-white text-[11px]">
          <th rowSpan={2} className="px-2 py-1.5 text-left w-8 border-r border-indigo-500">#</th>
          <th rowSpan={2} className="px-2 py-1.5 text-left w-32 border-r border-indigo-500">Category</th>
          <th rowSpan={2} className="px-2 py-1.5 text-left w-28 border-r border-indigo-500">Type of Work</th>
          <th rowSpan={2} className="px-2 py-1.5 text-left border-r border-indigo-500">Description</th>
          <th rowSpan={2} className="px-2 py-1.5 text-left w-28 border-r border-indigo-500">Size</th>
          <th rowSpan={2} className="px-2 py-1.5 text-right w-14 border-r border-indigo-500">Qty</th>
          <th rowSpan={2} className="px-2 py-1.5 text-left w-12 border-r border-indigo-500">Unit</th>
          {suppliers.map((s, i) => {
            const isWinner = winner && s.id === winner.id;
            return (
              <th key={s.id || i} colSpan={subCols}
                  className={`px-2 py-1.5 text-center border-l-2 ${isWinner ? 'bg-emerald-600 border-emerald-200' : 'border-white/30'}`}>
                <div className="font-semibold">{s.supplier_name || `Supplier #${i + 1}`}</div>
                {s.quote_no && <div className="font-mono text-[10px] opacity-80">{s.quote_no}</div>}
                {isWinner && <div className="text-[9px] mt-0.5 bg-emerald-200 text-emerald-900 inline-block px-1.5 py-0.5 rounded">🏆 Best Total</div>}
              </th>
            );
          })}
        </tr>
        {/* Sub-header row — dynamic by mode. */}
        <tr className="bg-indigo-500 text-white text-[10px] uppercase tracking-wider">
          {suppliers.map((s, i) => (
            <React.Fragment key={`sh-${s.id || i}`}>
              {mode !== 'install' && (
                <th className="px-2 py-1 text-right border-l-2 border-white/30 w-20">Unit Price</th>
              )}
              {(mode === 'install' || mode === 'both') && (
                <th className={`px-2 py-1 text-right w-20 ${mode === 'install' ? 'border-l-2 border-white/30' : ''}`}>
                  Unit Install
                </th>
              )}
              <th className="px-2 py-1 text-right w-20">Total</th>
              <th className="px-2 py-1 text-right w-16">Delivery</th>
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((it, idx) => {
          // Compute the row's type_of_work — prefer the item's own value;
          // fall back to any supplier row that set it for that item.
          const rowTow = it.type_of_work || (() => {
            for (const s of suppliers) {
              const k = s.id || s.supplier_id || s.supplier_name;
              const r = bySup[k]?.[it.no];
              if (r?.type_of_work) return r.type_of_work;
            }
            return null;
          })();
          return (
          <tr key={it.no} className={`border-t border-gray-100 align-top ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
            <td className="px-2 py-1.5 font-mono text-gray-500">{it.no}</td>
            <td className="px-2 py-1.5 font-mono text-[11px] text-gray-700">{formatCategory(it.category)}</td>
            <td className="px-2 py-1.5 text-[11px]">
              {rowTow ? (
                <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold text-[10px]">
                  {rowTow}
                </span>
              ) : (
                <span className="text-gray-400 italic">—</span>
              )}
            </td>
            <td className="px-2 py-1.5 whitespace-pre-wrap leading-snug">
              {it.description}
              {(it.requested_brand || it.brand_strictness) && (
                <span className="block text-[10px] text-gray-500 mt-0.5"
                      title={`Brand requested by client: ${it.requested_brand || '—'} (${it.brand_strictness || 'Open'})`}>
                  brand: <span className="font-mono">{it.requested_brand || '—'}</span>
                  {it.brand_strictness === 'Specified' && (
                    <span className="ml-1 px-1 rounded bg-rose-100 text-rose-700 font-semibold">strict</span>
                  )}
                </span>
              )}
            </td>
            <td className="px-2 py-1.5 whitespace-pre-wrap text-[11px] text-gray-700">{it.size || '—'}</td>
            <td className="px-2 py-1.5 text-right font-mono">{it.qty}</td>
            <td className="px-2 py-1.5 font-mono">{it.unit || '—'}</td>
            {suppliers.map((s, i) => {
              const k = s.id || s.supplier_id || s.supplier_name;
              const r = bySup[k]?.[it.no];
              const isBest = bestByRow[it.no] === i;
              const baseCls = `px-2 py-1.5 text-right font-mono ${i === 0 ? 'border-l-2 border-indigo-200' : 'border-l border-gray-200'}`;
              if (!r || !r.covered) {
                return (
                  <React.Fragment key={`c-${s.id || i}-${it.no}`}>
                    <td colSpan={subCols} className={`${baseCls} text-rose-500 text-center italic`}>
                      no quote
                    </td>
                  </React.Fragment>
                );
              }
              const cellTone = isBest ? 'bg-emerald-50 text-emerald-900 font-bold' : '';
              const rowTow   = r.type_of_work || tows[idx];
              const showSupply  = rowTow !== 'Installation';
              const showInstall = rowTow === 'Supply & Installation' || rowTow === 'Installation';
              return (
                <React.Fragment key={`c-${s.id || i}-${it.no}`}>
                  {mode !== 'install' && (
                    <td className={`${baseCls} ${cellTone}`}>
                      {showSupply
                        ? Number(r.unit_price || 0).toFixed(2)
                        : <span className="text-gray-400 italic">—</span>}
                    </td>
                  )}
                  {(mode === 'install' || mode === 'both') && (
                    <td className={`${baseCls.replace('border-l-2', 'border-l')} ${cellTone}`}
                        data-testid={`unified-install-${i}-${it.no}`}>
                      {showInstall && r.install_price != null
                        ? Number(r.install_price).toFixed(2)
                        : <span className="text-gray-400 italic">—</span>}
                    </td>
                  )}
                  <td className={`px-2 py-1.5 text-right font-mono ${cellTone}`}>{r.total.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[10px] text-gray-600">
                    {r.lead_time_days != null ? `${r.lead_time_days}d` : '—'}
                  </td>
                </React.Fragment>
              );
            })}
          </tr>
          );
        })}
        {/* Grand totals row */}
        <tr className="border-t-2 border-violet-300 bg-violet-50 font-semibold">
          <td colSpan={7} className="px-2 py-2 text-right text-violet-900">Grand Total</td>
          {suppliers.map((s, i) => {
            const isWinner = winner && s.id === winner.id;
            return (
              <React.Fragment key={`gt-${s.id || i}`}>
                {/* fillers for every sub-col except the "Total" position */}
                {Array.from({ length: subCols - 2 }).map((_, idx) => (
                  <td key={`gt-fill-${i}-${idx}`}
                      className={`px-2 py-2 ${idx === 0 && i === 0 ? 'border-l-2 border-indigo-200' : 'border-l border-gray-200'}`}></td>
                ))}
                <td className={`px-2 py-2 text-right font-mono ${isWinner ? 'bg-emerald-200 text-emerald-900 font-bold' : 'text-violet-900'}`}>
                  {(s.grand_total || 0).toFixed(2)}
                </td>
                <td className="px-2 py-2"></td>
              </React.Fragment>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
};


export default PriceComparison;
